import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Search,
  Package,
  Download,
  RefreshCw,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  X
} from 'lucide-react';
import api from '../utils/api';

interface SkuItem {
  sku: string;
  product_count: number;
  first_product_title: string;
  first_product_image?: string;
}

interface SkuComparisonLoaderProps {
  tableName: string;
  onLoadSkuProducts: (products: any[]) => void;
}

const SkuComparisonLoader: React.FC<SkuComparisonLoaderProps> = ({
  tableName,
  onLoadSkuProducts
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [skuList, setSkuList] = useState<SkuItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 10;

  // Debounce search
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch SKU list
  const fetchSkuList = useCallback(async () => {
    if (!tableName) return;

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        tableName,
        page: page.toString(),
        limit: limit.toString()
      });

      if (debouncedSearch) {
        params.append('search', debouncedSearch);
      }

      const response = await api.get(`/api/skus/list?${params}`);

      if (response.data.skus) {
        setSkuList(response.data.skus);
        setTotalPages(response.data.pagination?.totalPages || 1);
        setTotal(response.data.pagination?.total || 0);
      }
    } catch (err: any) {
      console.error('Error fetching SKU list:', err);
      setError(err.message || t('sku_comparison.error_loading'));
    } finally {
      setLoading(false);
    }
  }, [tableName, page, debouncedSearch, t]);

  useEffect(() => {
    fetchSkuList();
  }, [fetchSkuList]);

  // Load products for a SKU
  const handleLoadSku = async (sku: string) => {
    setLoadingProducts(sku);
    setError(null);

    try {
      // Fetch all products for this SKU (without pagination limit for comparison)
      const response = await api.get(`/api/skus/${encodeURIComponent(sku)}/products`, {
        params: {
          tableName,
          limit: 100 // Get up to 100 products for comparison
        }
      });

      if (response.data.products && response.data.products.length > 0) {
        // Add comparisonId to each product
        const productsWithComparisonId = response.data.products.map((product: any) => ({
          ...product,
          comparisonId: `${product.id_catalogo || product.ID_CATALOGO}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          _loadedFromSku: sku
        }));

        onLoadSkuProducts(productsWithComparisonId);
      } else {
        setError(t('sku_comparison.no_products_found'));
      }
    } catch (err: any) {
      console.error('Error loading SKU products:', err);
      setError(err.message || t('sku_comparison.error_loading_products'));
    } finally {
      setLoadingProducts(null);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center space-x-2 mb-3">
          <Package className="h-5 w-5 text-indigo-600" />
          <h3 className="text-lg font-semibold text-gray-900">
            {t('sku_comparison.title')}
          </h3>
          {total > 0 && (
            <span className="bg-indigo-100 text-indigo-800 text-xs px-2 py-1 rounded-full">
              {total} Grupos
            </span>
          )}
        </div>

        <p className="text-sm text-gray-600 mb-3">
          {t('sku_comparison.description')}
        </p>

        {/* Search input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('sku_comparison.search_placeholder')}
            className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
          />
          {searchTerm && (
            <button
              onClick={handleClearSearch}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="px-4 py-3 bg-red-50 border-b border-red-200">
          <div className="flex items-center space-x-2 text-red-700">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm">{error}</span>
          </div>
        </div>
      )}

      {/* SKU list */}
      <div className="divide-y divide-gray-200 max-h-80 overflow-y-auto">
        {loading ? (
          <div className="p-8 text-center">
            <RefreshCw className="h-6 w-6 text-indigo-500 animate-spin mx-auto mb-2" />
            <span className="text-gray-600 text-sm">{t('sku_comparison.loading')}</span>
          </div>
        ) : skuList.length === 0 ? (
          <div className="p-8 text-center">
            <Package className="h-10 w-10 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600 text-sm">
              {debouncedSearch
                ? t('sku_comparison.no_results')
                : t('sku_comparison.no_skus')}
            </p>
          </div>
        ) : (
          skuList.map((item) => (
            <div
              key={item.sku}
              className="p-3 hover:bg-gray-50 transition-colors flex items-center justify-between"
            >
              <div className="flex items-center space-x-3 min-w-0 flex-1">
                {item.first_product_image ? (
                  <img
                    src={item.first_product_image}
                    alt={item.sku}
                    className="w-10 h-10 object-contain rounded border border-gray-200 flex-shrink-0"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="w-10 h-10 bg-gray-100 rounded border border-gray-200 flex items-center justify-center flex-shrink-0">
                    <Package className="h-5 w-5 text-gray-400" />
                  </div>
                )}

                <div className="min-w-0 flex-1">
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-gray-900 text-sm">
                      {item.sku}
                    </span>
                    <span className="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded">
                      {item.product_count} {item.product_count === 1 ? t('sku_comparison.product') : t('sku_comparison.products')}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 truncate mt-0.5">
                    {item.first_product_title}
                  </p>
                </div>
              </div>

              <button
                onClick={() => handleLoadSku(item.sku)}
                disabled={loadingProducts === item.sku}
                className="ml-3 p-2 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-lg transition-colors disabled:opacity-50"
                title={t('sku_comparison.load_to_comparison')}
              >
                {loadingProducts === item.sku ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
              </button>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
          <span className="text-sm text-gray-600">
            {t('sku_comparison.page_info', { page, totalPages })}
          </span>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1 || loading}
              className="p-1 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages || loading}
              className="p-1 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SkuComparisonLoader;
